import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdvertisementAPI } from '../api/AdvertisementAPI';
import schickLogo from '../resources/images/schick-logo-white.png';
import './AdUploadForm.css';

function PhonePreview({ image, descriptionDE, actionUrl, advertiserName, lang, texts }) {
  return (
    <div className="auf-phone-wrap">
      <div className="auf-phone-label">{texts.phoneLabel}</div>
      <div className="auf-phone">
        <div className="auf-phone-frame">
          <div className="auf-phone-statusbar">
            <span>9:41</span>
            <span className="auf-phone-statusbar-icons">
              <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><path d="M1.5 8.5a13 13 0 0 1 21 0M5 12a10 10 0 0 1 14 0M8.5 15.5a6 6 0 0 1 7 0M12 19h.01"/></svg>
              <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><rect x="2" y="7" width="16" height="11" rx="2"/><path d="M22 11v3"/></svg>
            </span>
          </div>
          <div className="auf-phone-ad">
            {image ? (
              <img src={image} alt="" className="auf-phone-ad-img" />
            ) : (
              <div className="auf-phone-ad-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>{texts.phonePlaceholder}</span>
              </div>
            )}
            <div className="auf-phone-overlay" />
            <div className="auf-phone-ad-label">{texts.adLabel}</div>
            <button className="auf-phone-close" type="button" tabIndex={-1}>✕</button>
            <div className="auf-phone-content">
              <div className={`auf-phone-advertiser${!advertiserName ? ' auf-phone-advertiser--empty' : ''}`}>
                {advertiserName || texts.brandPlaceholder}
              </div>
              {descriptionDE ? (
                <p className="auf-phone-desc">{descriptionDE}</p>
              ) : (
                <p className="auf-phone-desc auf-phone-desc--empty">{texts.descPlaceholder}</p>
              )}
              <div className="auf-phone-cta">
                {texts.learnMore}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><path d="M9 18l6-6-6-6"/></svg>
              </div>
            </div>
          </div>
          <div className="auf-phone-home" />
        </div>
      </div>
      <div className="auf-phone-note">{texts.phoneNote}</div>
    </div>
  );
}

function ImageUploadSlot({ tag, tagColor, hint, required, optional, onChange, preview, onClear, removeLabel, dropLabel, dropSub }) {
  const ref = useRef(null);
  return (
    <div className="auf-img-slot">
      <div className="auf-img-slot-header">
        <span className={`auf-img-slot-tag auf-img-slot-tag--${tagColor}`}>{tag}</span>
        {required && <span className="auf-img-slot-required">{required}</span>}
        {optional && <span className="auf-img-slot-optional">{optional}</span>}
      </div>
      {hint !== undefined && <p className="auf-img-slot-hint">{hint}</p>}
      {preview ? (
        <div className="auf-img-slot-preview" onClick={() => ref.current?.click()}>
          <input ref={ref} type="file" accept="image/jpeg,image/png"
            style={{ display: 'none' }} onChange={e => onChange(e.target.files?.[0])} />
          <img src={preview} alt="Vorschau" className="auf-img-slot-img" />
          <div className="auf-img-slot-overlay">
            <button type="button" className="auf-img-slot-remove"
              onClick={e => { e.stopPropagation(); onClear(); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
              {removeLabel}
            </button>
          </div>
        </div>
      ) : (
        <div className="auf-img-slot-drop" onClick={() => ref.current?.click()}>
          <input ref={ref} type="file" accept="image/jpeg,image/png"
            style={{ display: 'none' }} onChange={e => onChange(e.target.files?.[0])} />
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <span className="auf-img-slot-drop-text">{dropLabel}</span>
          <span className="auf-img-slot-drop-sub">{dropSub}</span>
        </div>
      )}
    </div>
  );
}

export default function AdUploadForm() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [session, setSession]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [invalid, setInvalid]     = useState(false);
  const [expired, setExpired]     = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [submitStep, setSubmitStep]   = useState('');
  const [error, setError]         = useState(null);

  const [imageDE, setImageDE] = useState(null);
  const [imageEN, setImageEN] = useState(null);
  const [previewLang, setPreviewLang] = useState('DE');
  const [previewDE, setPreviewDE] = useState(null);
  const [previewEN, setPreviewEN] = useState(null);

  const [form, setForm] = useState({
    advertiserName: '',
    descriptionDE: '', descriptionEN: '',
    actionUrl: '',
    companyName: '', legalForm: '', street: '', zip: '', city: '',
    country: 'Österreich', vatId: '', invoiceEmail: '',
    contactPerson: '', phone: '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const t = {
    DE: {
      stepLabel:'Kampagnenstatus', steps:['Eingereicht','Prüfung','Rechnung','Live'],
      heroTitle:'Ihre Marke.', heroSpan:'Direkt im Spotlight.',
      heroDesc:'Platzieren Sie Ihre Werbeanzeige prominent in der Schick Vienna App und erreichen Sie eine lebendige Wiener Zielgruppe, die das Beste aus ihrer Stadt herausholen will.',
      impressions:'Impressionen', deadline:'Einreichung bis',
      card1:'Werbemittel', card1Sub:['Hochformat','720 × 1280 px min.','5 MB max.','JPG','PNG'],
      card2:'Anzeigeninhalt', card2Sub:['Max. 135 Zeichen','kurz und prägnant'],
      card3:'Rechnungsdaten', card3Sub:['Nur für Rechnungslegung','nicht veröffentlicht'],
      bgImage:'Hintergrundbild', tagDE:'Deutsch', tagEN:'Englisch',
      engHint:'Nur nötig wenn Ihr Bild Text enthält. Wird englischsprachigen Nutzern angezeigt.',
      adName:'Anzeigename', adNamePlaceholder:'z. B. Schick Vienna',
      adText:'Werbetext', targetLink:'Ziel-Link',
      targetLinkHint:'Ziel-URL bei Klick auf die Anzeige — z. B. Ihre Website, ein Angebot oder eine Buchungsseite.',
      targetLinkPlaceholder:'https://www.ihre-website.at/angebot', checkLink:'Prüfen',
      companySection:'Unternehmen', companyName:'Firmenname', legalForm:'Rechtsform',
      address:'Adresse', street:'Straße + Hausnummer', zip:'PLZ', city:'Ort',
      country:'Land', vatId:'UID-Nummer', contactSection:'Kontakt',
      invoiceEmail:'Rechnungs-E-Mail', contactPerson:'Ansprechpartner', phone:'Telefon',
      required:'Pflicht', optional:'Optional',
      submit:'Werbemittel einreichen', submitting:'Wird übermittelt…',
      stepUploadDE:'Lade Werbebild hoch…', stepUploadEN:'Lade englisches Werbebild hoch…', stepSubmitForm:'Übermittle Formular…',
      legal:'Mit dem Absenden bestätigen Sie, dass Sie berechtigt sind, diese Werbemittel einzureichen, und stimmen der Verwendung Ihrer Rechnungsdaten zur Rechnungslegung zu. Die Anzeige wird nach Zahlungseingang geschaltet.',
      phoneLabel:'Live-Vorschau', phoneNote:'So sieht Ihre Anzeige in der App aus',
      phonePlaceholder:'Werbebild erscheint hier', brandPlaceholder:'Ihr Markenname',
      descPlaceholder:'Ihr Werbetext erscheint hier …', adLabel:'Anzeige', learnMore:'Mehr erfahren',
      removeLabel:'Entfernen', dropLabel:'Bild hochladen', dropSub:'JPG · PNG · mind. 720 × 1280 px · max. 5 MB',
      footerNote:'Ihre Rechnungsdaten werden ausschließlich zur Rechnungslegung verwendet und nicht veröffentlicht.',
      footerContact:'Kontakt',
      invalidTitle:'Link ungültig', invalidSub:'Dieser Link existiert nicht. Bitte wende dich an dein Schick Vienna Kontakt.',
      expiredTitle:'Link abgelaufen', expiredSub:'Diese Upload-Session ist nicht mehr aktiv. Bitte fordere einen neuen Link an.',
      errNoImg:'Bitte lade das Werbebild hoch.', errNoName:'Anzeigename ist erforderlich.',
      errNameLen:'Anzeigename darf maximal 255 Zeichen lang sein.',
      errNoDescDE:'Bitte trage den deutschen Werbetext ein.', errNoDescEN:'Bitte trage den englischen Werbetext ein.',
      errNoCompany:'Firmenname ist erforderlich.', errNoLegalForm:'Rechtsform ist erforderlich.',
      errLegalFormLen:'Rechtsform darf maximal 100 Zeichen lang sein.',
      errNoStreet:'Straße ist erforderlich.', errNoZip:'PLZ ist erforderlich.',
      errZipLen:'PLZ darf maximal 20 Zeichen lang sein.', errNoCity:'Ort ist erforderlich.',
      errNoCountry:'Land ist erforderlich.', errNoInvoiceEmail:'Rechnungs-E-Mail ist erforderlich.',
      errInvalidEmail:'Bitte eine gültige E-Mail-Adresse eingeben.', errNoContact:'Ansprechpartner ist erforderlich.',
      errPhoneLen:'Telefonnummer darf maximal 50 Zeichen lang sein.', errGeneric:'Fehler beim Senden. Bitte versuche es erneut.',
      errUrl:'Ziel-Link ist erforderlich.', errUrlProtocol:'Ziel-Link muss http oder https sein.',
      errUrlLocal:'Dieser Ziel-Link ist nicht erlaubt.', errUrlInvalid:'Bitte eine gültige URL eingeben.',
    },
    EN: {
      stepLabel:'Campaign Status', steps:['Submitted','Review','Invoice','Live'],
      heroTitle:'Your Brand.', heroSpan:'Directly in the Spotlight.',
      heroDesc:'Place your advertisement prominently in the Schick Vienna App and reach a vibrant Viennese audience eager to get the most out of their city.',
      impressions:'Impressions', deadline:'Submit by',
      card1:'Ad Creative', card1Sub:['Portrait format','720 × 1280 px min.','5 MB max.','JPG','PNG'],
      card2:'Ad Content', card2Sub:['Max. 135 characters','short and concise'],
      card3:'Billing Details', card3Sub:['For invoicing only','not published'],
      bgImage:'Background Image', tagDE:'German', tagEN:'English',
      engHint:'Only needed if your image contains text. Shown to English-speaking users.',
      adName:'Display Name', adNamePlaceholder:'e.g. Schick Vienna',
      adText:'Ad Text', targetLink:'Destination Link',
      targetLinkHint:'URL when the ad is clicked — e.g. your website, an offer or a booking page.',
      targetLinkPlaceholder:'https://www.your-website.com/offer', checkLink:'Check',
      companySection:'Company', companyName:'Company Name', legalForm:'Legal Form',
      address:'Address', street:'Street + Number', zip:'ZIP Code', city:'City',
      country:'Country', vatId:'VAT Number', contactSection:'Contact',
      invoiceEmail:'Invoice Email', contactPerson:'Contact Person', phone:'Phone',
      required:'Required', optional:'Optional',
      submit:'Submit Ad Materials', submitting:'Submitting…',
      stepUploadDE:'Uploading ad image…', stepUploadEN:'Uploading English ad image…', stepSubmitForm:'Submitting form…',
      legal:'By submitting you confirm that you are authorized to submit these advertising materials and agree to the use of your billing data for invoicing. The ad will go live after payment is received.',
      phoneLabel:'Live Preview', phoneNote:'How your ad looks in the app',
      phonePlaceholder:'Ad image appears here', brandPlaceholder:'Your brand name',
      descPlaceholder:'Your ad text appears here …', adLabel:'Ad', learnMore:'Learn more',
      removeLabel:'Remove', dropLabel:'Upload image', dropSub:'JPG · PNG · min. 720 × 1280 px · max. 5 MB',
      footerNote:'Your billing data is used exclusively for invoicing and will not be published.',
      footerContact:'Contact',
      invalidTitle:'Invalid Link', invalidSub:'This link does not exist. Please contact your Schick Vienna representative.',
      expiredTitle:'Link Expired', expiredSub:'This upload session is no longer active. Please request a new link.',
      errNoImg:'Please upload the ad image.', errNoName:'Display name is required.',
      errNameLen:'Display name must not exceed 255 characters.',
      errNoDescDE:'Please enter the German ad text.', errNoDescEN:'Please enter the English ad text.',
      errNoCompany:'Company name is required.', errNoLegalForm:'Legal form is required.',
      errLegalFormLen:'Legal form must not exceed 100 characters.',
      errNoStreet:'Street is required.', errNoZip:'ZIP code is required.',
      errZipLen:'ZIP code must not exceed 20 characters.', errNoCity:'City is required.',
      errNoCountry:'Country is required.', errNoInvoiceEmail:'Invoice email is required.',
      errInvalidEmail:'Please enter a valid email address.', errNoContact:'Contact person is required.',
      errPhoneLen:'Phone number must not exceed 50 characters.', errGeneric:'Submission error. Please try again.',
      errUrl:'Destination link is required.', errUrlProtocol:'Destination link must use http or https.',
      errUrlLocal:'This destination link is not allowed.', errUrlInvalid:'Please enter a valid URL.',
    },
  }[previewLang];

  // ── Validation helpers (mirrors backend AdvertisementValidator) ───────────
  const validateImageFile = (file) => new Promise((resolve, reject) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      reject('Nur JPG und PNG sind erlaubt.'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      reject('Bild darf maximal 5 MB groß sein.'); return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width < 720 || img.height < 1280)
        reject(`Bild muss mindestens 720 × 1280 px sein (aktuell ${img.width} × ${img.height} px).`);
      else if (img.width > 3000 || img.height > 6000)
        reject('Bild ist zu groß (max. 3000 × 6000 px).');
      else resolve();
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject('Bild konnte nicht gelesen werden.'); };
    img.src = url;
  });

  const validateActionUrl = (url) => {
    if (!url.trim()) return t.errUrl;
    try {
      const u = new URL(url.trim());
      if (!['http:', 'https:'].includes(u.protocol)) return t.errUrlProtocol;
      const host = u.hostname.toLowerCase();
      if (['localhost', '127.0.0.1', '0.0.0.0'].includes(host) || host.endsWith('.local'))
        return t.errUrlLocal;
      return null;
    } catch { return t.errUrlInvalid; }
  };

  const validateEmailFormat = (email) =>
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  useEffect(() => {
    AdvertisementAPI.getPublicSession(token)
      .then(s => setSession(s))
      .catch(err => {
        const status = err?.response?.status;
        if (status === 410) navigate(`/advertisements/reports/${token}`, { replace: true });
        else setInvalid(true);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleImageDE = async (file) => {
    if (!file) { setImageDE(null); setPreviewDE(null); return; }
    try {
      await validateImageFile(file);
      setImageDE(file); setPreviewDE(URL.createObjectURL(file));
    } catch (msg) { setError(msg); }
  };

  const handleImageEN = async (file) => {
    if (!file) { setImageEN(null); setPreviewEN(null); return; }
    try {
      await validateImageFile(file);
      setImageEN(file); setPreviewEN(URL.createObjectURL(file));
    } catch (msg) { setError(msg); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageDE) { setError(t.errNoImg); return; }
    if (!form.advertiserName.trim()) { setError(t.errNoName); return; }
    if (form.advertiserName.length > 255) { setError(t.errNameLen); return; }
    if (!form.descriptionDE.trim()) { setError(t.errNoDescDE); return; }
    if (!form.descriptionEN.trim()) { setError(t.errNoDescEN); return; }
    const urlErr = validateActionUrl(form.actionUrl);
    if (urlErr) { setError(urlErr); return; }
    if (!form.companyName.trim()) { setError(t.errNoCompany); return; }
    if (!form.legalForm.trim()) { setError(t.errNoLegalForm); return; }
    if (form.legalForm.length > 100) { setError(t.errLegalFormLen); return; }
    if (!form.street.trim()) { setError(t.errNoStreet); return; }
    if (!form.zip.trim()) { setError(t.errNoZip); return; }
    if (form.zip.length > 20) { setError(t.errZipLen); return; }
    if (!form.city.trim()) { setError(t.errNoCity); return; }
    if (!form.country.trim()) { setError(t.errNoCountry); return; }
    if (!form.invoiceEmail.trim()) { setError(t.errNoInvoiceEmail); return; }
    if (!validateEmailFormat(form.invoiceEmail)) { setError(t.errInvalidEmail); return; }
    if (!form.contactPerson.trim()) { setError(t.errNoContact); return; }
    if (form.phone && form.phone.length > 50) { setError(t.errPhoneLen); return; }

    try {
      setSubmitting(true); setError(null);

      // Step 1: upload DE image
      setSubmitStep(t.stepUploadDE);
      const assetDE = await AdvertisementAPI.uploadAsset(token, imageDE, 'DE');

      let assetENId = null;
      if (imageEN) {
        setSubmitStep(t.stepUploadEN);
        const assetEN = await AdvertisementAPI.uploadAsset(token, imageEN, 'EN');
        assetENId = assetEN.id;
      }

      setSubmitStep(t.stepSubmitForm);
      await AdvertisementAPI.submitAd(token, {
        advertisementImageDEAssetId: assetDE.id,
        advertisementImageENAssetId: assetENId,
        displayName: form.advertiserName.trim() || null,
        descriptionDE: form.descriptionDE.trim(),
        descriptionEN: form.descriptionEN.trim() || null,
        actionUrl: form.actionUrl.trim(),
        companyName: form.companyName.trim(),
        legalForm: form.legalForm.trim(),
        street: form.street.trim(),
        zip: form.zip.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
        vatId: form.vatId.trim() || null,
        invoiceEmail: form.invoiceEmail.trim(),
        contactPerson: form.contactPerson.trim(),
        phone: form.phone.trim() || null,
      });

      navigate(`/advertisements/reports/${token}`, {
        state: {
          previewDE:   previewDE,
          previewEN:   previewEN,
          displayName: form.advertiserName.trim(),
          descriptionDE: form.descriptionDE.trim(),
        },
      });
    } catch (ex) {
      const status = ex?.response?.status;
      if (status === 409) { navigate(`/advertisements/reports/${token}`); return; }
      if (status === 410) { setExpired(true); return; }
      setError(ex?.response?.data?.message || t.errGeneric);
    } finally {
      setSubmitting(false); setSubmitStep('');
    }
  };

  // ── State screens ──────────────────────────────────────────────────────────

  if (loading) return (
    <div className="auf-page"><div className="auf-center"><div className="auf-spinner" /></div></div>
  );

  if (invalid) return (
    <div className="auf-page"><div className="auf-center">
      <div className="auf-state-icon auf-state-icon--error">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      </div>
      <h2 className="auf-state-title">{t.invalidTitle}</h2>
      <p className="auf-state-sub">{t.invalidSub}</p>
    </div></div>
  );

  if (expired) return (
    <div className="auf-page"><div className="auf-center">
      <div className="auf-state-icon auf-state-icon--warn">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      </div>
      <h2 className="auf-state-title">{t.expiredTitle}</h2>
      <p className="auf-state-sub">{t.expiredSub}</p>
    </div></div>
  );

  // ── Form ──────────────────────────────────────────────────────────────────

  return (
    <div className="auf-page">

      {/* ══ HEADER ══ */}
      <header className="auf-anim-fade auf-d0" style={{ position:'sticky', top:0, zIndex:10, background:'rgba(13,20,16,0.92)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)' }}>
        <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 40px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <img src={schickLogo} alt="Schick Vienna" style={{ height:46, width:'auto', mixBlendMode:'screen', opacity:0.88 }}/>
          <div style={{ display:'flex', alignItems:'center', gap:0 }}>
            {[{l:'DE',label:'Deutsch'},{l:'EN',label:'English'}].map(({l,label}, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span style={{ width:1, height:13, background:'rgba(255,255,255,0.12)', flexShrink:0 }}/>}
                <button type="button" onClick={() => setPreviewLang(l)} style={{ padding:'4px 14px', border:'none', background:'transparent', color: previewLang===l ? 'rgba(225,240,232,0.92)' : 'rgba(200,218,208,0.38)', fontSize:12, fontWeight:400, cursor:'pointer', letterSpacing:'0.02em', fontFamily:'inherit', transition:'color 0.18s' }}>
                  {label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div style={{ margin:'0 40px', height:2, borderRadius:9999, background:'rgba(255,255,255,0.08)' }}/>
      </header>

      {/* ══ HERO ══ */}
      <div className="auf-hero">
        <div className="auf-hero-inner">

<h1 className="auf-hero-title auf-anim auf-d1">
            {t.heroTitle}<br /><span>{t.heroSpan}</span>
          </h1>

          <p className="auf-hero-desc auf-anim auf-d2">{t.heroDesc}</p>

          <div className="auf-hero-badges auf-anim auf-d3">
            {session?.clientName && (
              <span className="auf-badge auf-badge--green">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {session.clientName}
              </span>
            )}
            {session?.impressions && (
              <span className="auf-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                {session.impressions.toLocaleString('de-AT')} {t.impressions}
              </span>
            )}
            {session?.expiresAt && (
              <span className="auf-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {t.deadline} {new Date(session.expiresAt).toLocaleDateString(previewLang === 'EN' ? 'en-GB' : 'de-AT')}
              </span>
            )}
          </div>

          <div className="auf-anim auf-d4" style={{ fontSize:9, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(200,218,208,0.5)', marginBottom:16, textAlign:'center' }}>
            {t.stepLabel}
          </div>
          <div className="auf-anim auf-d4" style={{ display:'flex', alignItems:'flex-start', gap:0, maxWidth:380, margin:'0 auto' }}>
            {t.steps.map((label, i) => {
              const active = i === 0;
              const isLast = i === 3;
              return (
                <React.Fragment key={label}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:9, flexShrink:0, width:64 }}>
                    <div style={{
                      width:30, height:30, borderRadius:'50%',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      background: active ? 'rgba(255,255,255,0.07)' : 'transparent',
                      border: active
                        ? '1.5px solid rgba(255,255,255,0.32)'
                        : '1.5px solid rgba(255,255,255,0.15)',
                    }}>
                      <span style={{ fontSize:10.5, fontWeight:500, color: active ? 'rgba(215,232,222,0.82)' : 'rgba(200,218,208,0.35)', fontVariantNumeric:'tabular-nums' }}>{i+1}</span>
                    </div>
                    <span style={{ fontSize:10, fontWeight: active ? 500 : 400, color: active ? 'rgba(215,232,222,0.85)' : 'rgba(200,218,208,0.4)', textAlign:'center', lineHeight:1.3 }}>
                      {label}
                    </span>
                  </div>
                  {!isLast && (
                    <div style={{ flex:1, height:1, marginTop:15, background:'rgba(255,255,255,0.12)' }}/>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* ══ FORM + PREVIEW ══ */}
      <div className="auf-layout">
        <div className="auf-container">
        <form className="auf-form" onSubmit={handleSubmit}>
          {/* ── Werbemittel ── */}
          <div className="auf-card auf-anim auf-d3">
            <div className="auf-card-header">
              <div className="auf-card-icon auf-card-icon--green">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              </div>
              <div>
                <div className="auf-card-title">{t.card1}</div>
                <div className="auf-card-sub" style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'wrap', marginTop:4 }}>
                  {t.card1Sub.map(s => (
                    <span key={s} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:5, padding:'2px 7px', fontSize:10.5, color:'rgba(210,228,218,0.7)', fontWeight:500, letterSpacing:'0.01em' }}>{s}</span>
                  ))}
                </div>
              </div>
            </div>

            <label className="auf-label">{t.bgImage}</label>
            <div className="auf-img-grid">
              <ImageUploadSlot
                tag={t.tagDE} tagColor="green" required={t.required}
                hint=""
                onChange={handleImageDE} preview={previewDE} onClear={() => handleImageDE(null)}
                removeLabel={t.removeLabel} dropLabel={t.dropLabel} dropSub={t.dropSub}
              />
              <ImageUploadSlot
                tag={t.tagEN} tagColor="blue" optional={t.optional}
                hint={t.engHint}
                onChange={handleImageEN} preview={previewEN} onClear={() => handleImageEN(null)}
                removeLabel={t.removeLabel} dropLabel={t.dropLabel} dropSub={t.dropSub}
              />
            </div>
          </div>

          {/* ── Anzeigeninhalt ── */}
          <div className="auf-card auf-anim auf-d4">
            <div className="auf-card-header">
              <div className="auf-card-icon auf-card-icon--orange">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </div>
              <div>
                <div className="auf-card-title">{t.card2}</div>
                <div className="auf-card-sub" style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'wrap', marginTop:4 }}>
                  {t.card2Sub.map(s => (
                    <span key={s} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:5, padding:'2px 7px', fontSize:10.5, color:'rgba(210,228,218,0.7)', fontWeight:500, letterSpacing:'0.01em' }}>{s}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="auf-field">
              <div className="auf-img-slot-header">
                <label className="auf-label">{t.adName}</label>
                <span className="auf-img-slot-required">{t.required}</span>
              </div>
              <input className="auf-input" value={form.advertiserName}
                onChange={e => set('advertiserName', e.target.value)}
                placeholder={t.adNamePlaceholder} maxLength={40} />
            </div>

            <div className="auf-field">
              <label className="auf-label auf-label--muted">{t.adText}</label>
              <div className="auf-label-row">
                <div className="auf-img-slot-header">
                  <span className="auf-img-slot-tag auf-img-slot-tag--green">{t.tagDE}</span>
                  <span className="auf-img-slot-required">{t.required}</span>
                </div>
                <span className={`auf-char-count${form.descriptionDE.length >= 125 ? ' auf-char-count--warn' : ''}`}>
                  {form.descriptionDE.length}/135
                </span>
              </div>
              <textarea className="auf-textarea" value={form.descriptionDE} maxLength={135}
                onChange={e => set('descriptionDE', e.target.value)}
                placeholder={previewLang === 'EN' ? 'e.g. "Free entry tonight with the Schick App."' : 'z. B. „Freier Eintritt heute Nacht mit der Schick App."'} />
            </div>
            <div className="auf-field">
              <label className="auf-label auf-label--muted">{t.adText}</label>
              <div className="auf-label-row">
                <div className="auf-img-slot-header">
                  <span className="auf-img-slot-tag auf-img-slot-tag--blue">{t.tagEN}</span>
                  <span className="auf-img-slot-required">{t.required}</span>
                </div>
                <span className={`auf-char-count${form.descriptionEN.length >= 125 ? ' auf-char-count--warn' : ''}`}>
                  {form.descriptionEN.length}/135
                </span>
              </div>
              <textarea className="auf-textarea" value={form.descriptionEN} maxLength={135}
                onChange={e => set('descriptionEN', e.target.value)}
                placeholder={previewLang === 'EN' ? 'e.g. "Free entry tonight with the Schick App."' : 'z. B. „Freier Eintritt heute Nacht mit der Schick App."'} />
            </div>
            <div className="auf-field">
              <div className="auf-img-slot-header">
                <label className="auf-label">{t.targetLink}</label>
                <span className="auf-img-slot-required">{t.required}</span>
              </div>
              <div className="auf-field-hint">{t.targetLinkHint}</div>
              <div className="auf-url-row">
                <input className="auf-input" type="url" value={form.actionUrl}
                  onChange={e => set('actionUrl', e.target.value)}
                  placeholder={t.targetLinkPlaceholder} />
                {form.actionUrl && (() => { try { new URL(form.actionUrl); return true; } catch { return false; } })() && (
                  <a className="auf-url-check" href={form.actionUrl} target="_blank" rel="noreferrer">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                    {t.checkLink}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* ── Rechnungsdaten ── */}
          <div className="auf-card auf-anim auf-d5">
            <div className="auf-card-header">
              <div className="auf-card-icon auf-card-icon--blue">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              </div>
              <div>
                <div className="auf-card-title">{t.card3}</div>
                <div className="auf-card-sub" style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'wrap', marginTop:4 }}>
                  {t.card3Sub.map(s => (
                    <span key={s} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:5, padding:'2px 7px', fontSize:10.5, color:'rgba(210,228,218,0.7)', fontWeight:500, letterSpacing:'0.01em' }}>{s}</span>
                  ))}
                </div>
              </div>
            </div>

            <label className="auf-label auf-label--muted">{t.companySection}</label>
            <div className="auf-row">
              <div className="auf-field">
                <div className="auf-img-slot-header">
                  <label className="auf-label">{t.companyName}</label>
                  <span className="auf-img-slot-required">{t.required}</span>
                </div>
                <input className="auf-input" value={form.companyName}
                  onChange={e => set('companyName', e.target.value)} placeholder="ACME GmbH" />
              </div>
              <div className="auf-field">
                <div className="auf-img-slot-header">
                  <label className="auf-label">{t.legalForm}</label>
                  <span className="auf-img-slot-required">{t.required}</span>
                </div>
                <input className="auf-input" value={form.legalForm}
                  onChange={e => set('legalForm', e.target.value)} placeholder="GmbH / OG / KG …" />
              </div>
            </div>

            <label className="auf-label auf-label--muted">{t.address}</label>
            <div className="auf-field">
              <div className="auf-img-slot-header">
                <label className="auf-label">{t.street}</label>
                <span className="auf-img-slot-required">{t.required}</span>
              </div>
              <input className="auf-input" value={form.street}
                onChange={e => set('street', e.target.value)} placeholder="Mariahilfer Straße 1" />
            </div>

            <div className="auf-row auf-row--3">
              <div className="auf-field">
                <div className="auf-img-slot-header">
                  <label className="auf-label">{t.zip}</label>
                  <span className="auf-img-slot-required">{t.required}</span>
                </div>
                <input className="auf-input" value={form.zip}
                  onChange={e => set('zip', e.target.value)} placeholder="1060" />
              </div>
              <div className="auf-field" style={{ gridColumn: 'span 2' }}>
                <div className="auf-img-slot-header">
                  <label className="auf-label">{t.city}</label>
                  <span className="auf-img-slot-required">{t.required}</span>
                </div>
                <input className="auf-input" value={form.city}
                  onChange={e => set('city', e.target.value)} placeholder="Wien" />
              </div>
            </div>

            <div className="auf-row">
              <div className="auf-field">
                <div className="auf-img-slot-header">
                  <label className="auf-label">{t.country}</label>
                  <span className="auf-img-slot-required">{t.required}</span>
                </div>
                <input className="auf-input" value={form.country}
                  onChange={e => set('country', e.target.value)} />
              </div>
              <div className="auf-field">
                <div className="auf-img-slot-header">
                  <label className="auf-label">{t.vatId}</label>
                  <span className="auf-img-slot-optional">{t.optional}</span>
                </div>
                <input className="auf-input" value={form.vatId}
                  onChange={e => set('vatId', e.target.value)} placeholder="ATU12345678" />
              </div>
            </div>

            <label className="auf-label auf-label--muted">{t.contactSection}</label>
            <div className="auf-row">
              <div className="auf-field">
                <div className="auf-img-slot-header">
                  <label className="auf-label">{t.invoiceEmail}</label>
                  <span className="auf-img-slot-required">{t.required}</span>
                </div>
                <input className="auf-input" type="email" value={form.invoiceEmail}
                  onChange={e => set('invoiceEmail', e.target.value)} placeholder="rechnung@firma.at" />
              </div>
              <div className="auf-field">
                <div className="auf-img-slot-header">
                  <label className="auf-label">{t.contactPerson}</label>
                  <span className="auf-img-slot-required">{t.required}</span>
                </div>
                <input className="auf-input" value={form.contactPerson}
                  onChange={e => set('contactPerson', e.target.value)} placeholder="Max Mustermann" />
              </div>
            </div>

            <div className="auf-field">
              <div className="auf-img-slot-header">
                <label className="auf-label">{t.phone}</label>
                <span className="auf-img-slot-optional">{t.optional}</span>
              </div>
              <input className="auf-input" type="tel" value={form.phone}
                onChange={e => set('phone', e.target.value)} placeholder="+43 660 1234567" />
            </div>
          </div>

          {/* ── Submit ── */}
          <div className="auf-submit-block">
            {error && (
              <div className="auf-error-banner">
                <div className="auf-error-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <span className="auf-error-text">{error}</span>
                <button type="button" className="auf-error-close" onClick={() => setError(null)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            )}
            <button className="auf-submit" type="submit" disabled={submitting}>
              {submitting ? (
                <><div className="auf-spinner-sm" />{submitStep || t.submitting}</>
              ) : (
                <>
                  {t.submit}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </>
              )}
            </button>
            <p className="auf-legal">{t.legal}</p>
          </div>

        </form>
        </div>

        {/* Sticky phone preview */}
        <div className="auf-preview-col auf-anim-right auf-d3">
          <PhonePreview
            image={previewLang === 'EN' && previewEN ? previewEN : previewDE}
            descriptionDE={previewLang === 'EN' ? form.descriptionEN : form.descriptionDE}
            actionUrl={form.actionUrl}
            advertiserName={form.advertiserName}
            lang={previewLang}
            texts={t}
          />
        </div>
      </div>

      {/* ══ FOOTER ══ */}
      <footer className="auf-anim-fade auf-d7" style={{ background:'#111a15', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth:1200, margin:'0 auto', padding:'44px 40px 40px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:40 }}>
            <p style={{ margin:0, fontSize:12, color:'rgba(200,218,208,0.45)', lineHeight:1.7, maxWidth:340 }}>
              {t.footerNote}
            </p>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontSize:10, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(200,218,208,0.3)', marginBottom:10 }}>
                {t.footerContact}
              </div>
              <a href="mailto:office@schick-vienna.at" style={{ fontSize:12, color:'rgba(200,218,208,0.55)', textDecoration:'none', display:'block' }}>
                office@schick-vienna.at
              </a>
            </div>
          </div>
          <div style={{ marginTop:36, paddingTop:20, borderTop:'1px solid rgba(255,255,255,0.05)' }}>
            <span style={{ fontSize:11, color:'rgba(200,218,208,0.25)' }}>
              © {new Date().getFullYear()} Schick Vienna
            </span>
          </div>
        </div>
      </footer>

    </div>
  );
}
